// Generated by ReScript, PLEASE EDIT WITH CARE

import * as I18n from "../../primitives/I18n.res.js";
import * as Page from "../../resources/layout-and-structure/Page.res.js";
import * as Group from "../../resources/layout-and-structure/Group.res.js";
import * as Stack from "../../resources/layout-and-structure/Stack.res.js";
import * as React from "react";
import * as Future from "rescript-future/src/Future.res.js";
import * as Inline from "../../resources/layout-and-structure/Inline.res.js";
import * as Notifier from "../../bundles/Notifier/Notifier.res.js";
import * as OrderPdf from "../../modules/Order/OrderPdf.res.js";
import * as OrderCart from "../../modules/Order/OrderCart.res.js";
import * as OrderEdit from "./OrderEdit.res.js";
import * as Navigation from "../../primitives/Navigation.res.js";
import * as OrderRoutes from "./OrderRoutes.res.js";
import * as OrderStatus from "../../modules/Order/OrderStatus.res.js";
import * as OrderEditForm from "../../modules/Order/OrderEditForm.res.js";
import * as OrderMemoCard from "../../modules/Order/OrderMemoCard.res.js";
import * as Stdlib_Option from "@rescript/runtime/lib/es6/Stdlib_Option.js";
import * as TriggerDownload from "../../primitives/TriggerDownload.res.js";
import * as OrderPageActions from "../../modules/Order/OrderPageActions.res.js";
import * as OrderStatusBadges from "../../modules/Order/OrderStatusBadges.res.js";
import * as JsxRuntime from "react/jsx-runtime";
import * as Accounting__Reducer from "@wino/accounting/src/Accounting__Reducer.res.js";
import * as OrderEditActionsList from "../../modules/Order/OrderEditActionsList.res.js";
import * as OrderInformationCard from "../../modules/Order/OrderInformationCard.res.js";
import * as OrderCartCsvExporting from "../../modules/Order/OrderCartCsvExporting.res.js";
import * as OrderConditionAndNotesCard from "../../modules/Order/OrderConditionAndNotesCard.res.js";
import * as OrderSupplierInformationCard from "../../modules/Order/OrderSupplierInformationCard.res.js";

function OrderEditPage(props) {
  let onRequestCartRowsError = props.onRequestCartRowsError;
  let statuses = props.statuses;
  let updatedAt = props.updatedAt;
  let name = props.name;
  let id = props.id;
  let match = Navigation.useUrl();
  let pathname = match.pathname;
  let notifier = Notifier.use(undefined, undefined);
  let state = OrderEditForm.useFormState();
  let dispatch = OrderEditForm.useFormDispatch();
  let match$1 = React.useState(() => OrderEdit.isInitialFullEdition(statuses, Stdlib_Option.isSome(updatedAt)));
  let setEdition = match$1[1];
  let edition = match$1[0];
  let match$2 = React.useState(() => false);
  let setEditedMemo = match$2[1];
  let editedMemo = match$2[0];
  React.useEffect(() => {
    if (!edition) {
      notifier.clear();
      onRequestCartRowsError([]);
    }
  }, [
    Stdlib_Option.map(updatedAt, prim => prim.getTime()),
    edition
  ]);
  React.useEffect(() => {
    if (OrderStatus.has(statuses, "RECEIVING")) {
      setEdition(param => true);
    }
  }, [OrderStatus.has(statuses, "RECEIVING")]);
  React.useEffect(() => {
    let match = state.submission;
    if (typeof match === "object" && match.TAG === "Succeeded") {
      setEdition(param => false);
    }
  }, [state.submission]);
  React.useEffect(() => {
    if (state.status !== "Pristine" && state.submission !== "Requested") {
      setEdition(param => true);
    } else {
      setEditedMemo(param => false);
    }
  }, [state.status]);
  React.useEffect(() => {
    if (!edition && state.values.note !== state.initialValues.note) {
      setEditedMemo(param => true);
    }
  }, [state.values.note]);
  let onRequestDispatch = action => dispatch({
    TAG: "FieldValueChanged",
    _0: "Cart",
    _1: prevCart => Accounting__Reducer.reducer(prevCart, action)
  });
  let renderPageTitleEnd = () => {
    let match = edition && !editedMemo;
    if (match) {
      return null;
    } else {
      return JsxRuntime.jsx(OrderStatusBadges.make, {
        statuses: statuses
      });
    }
  };
  let handlesActionList = React.useCallback(action => {
    switch (action) {
      case "Edition" :
        return setEdition(param => true);
      case "DownloadPDF" :
        let orderId = state.id;
        if (orderId !== undefined) {
          Future.tapError(OrderPdf.requestAndOpen(orderId, name), param => notifier.add({
            TAG: "Error",
            _0: OrderPdf.errorMessage
          }, undefined, undefined));
          return notifier.clear();
        } else {
          return;
        }
      case "DownloadCSV" :
        let csvBlob = OrderCartCsvExporting.makeBlob(state.values.cart, name, statuses, state.values.supplierName, state.values.supplierAddress, state.values.supplierPostalCode, state.values.supplierCity, state.values.supplierCountry);
        if (csvBlob.TAG !== "Ok") {
          return notifier.add({
            TAG: "Error",
            _0: "Something went wrong"
          }, undefined, undefined);
        }
        Future.tapError(TriggerDownload.fromBlob(csvBlob._0, OrderCartCsvExporting.makeFilename(statuses, name)), () => notifier.add({
          TAG: "Error",
          _0: "Something went wrong"
        }, undefined, undefined));
        return;
    }
  }, [
    state.id,
    state.values.cart,
    name,
    statuses
  ]);
  let renderPageActions = () => JsxRuntime.jsxs(Inline.make, {
    children: [
      edition ? null : JsxRuntime.jsx(OrderEditActionsList.make, {
          id: id,
          statuses: statuses,
          onRequestAction: handlesActionList
        }),
      JsxRuntime.jsx(OrderPageActions.make, {
        id: id,
        statuses: statuses,
        edition: edition,
        onRequestCancelEdition: () => setEdition(param => false)
      })
    ],
    space: "small"
  });
  let shouldBlockOnRouteChange = React.useCallback(nextRoute => {
    let allowed = pathname === OrderRoutes.baseRoute + "/create" && nextRoute.startsWith(OrderRoutes.baseRoute + "/");
    if (allowed) {
      return false;
    } else {
      return state.status !== "Pristine";
    }
  }, [state.status]);
  let isDraft = OrderStatus.has(statuses, "DRAFT");
  let isPaid = OrderStatus.has(statuses, "PAID");
  let isFullEditing = edition && !editedMemo;
  let editable = (isDraft || isFullEditing) && !isPaid;
  let match$3 = statuses[0];
  let tmp;
  let exit = 0;
  if (match$3 === "RECEIVING" && editable) {
    tmp = I18n.t("Reception");
  } else {
    exit = 1;
  }
  if (exit === 1) {
    tmp = edition && !editedMemo ? I18n.t("Edition") : " ";
  }
  let formattedTitle = I18n.template(I18n.t("Order{{entilted}}{{name}}"), {
    entilted: tmp,
    name: I18n.t(name)
  }, undefined);
  return JsxRuntime.jsxs(JsxRuntime.Fragment, {
    children: [
      JsxRuntime.jsx(Navigation.Prompt.make, {
        message: I18n.t("Some changes in the order have not been saved."),
        shouldBlockOnRouteChange: shouldBlockOnRouteChange
      }),
      JsxRuntime.jsx(Page.make, {
        children: JsxRuntime.jsxs(Stack.make, {
          children: [
            JsxRuntime.jsx(Notifier.Banner.make, {
              notifier: notifier
            }),
            JsxRuntime.jsx(OrderCart.make, {
              orderId: id,
              cart: state.values.cart,
              statuses: statuses,
              editable: editable,
              rowErrors: props.cartRowErrors,
              edition: edition,
              onRequestCancelEdition: () => setEdition(param => false),
              onRequestCartRowsError: onRequestCartRowsError,
              onRequestDispatch: onRequestDispatch
            }),
            JsxRuntime.jsxs(Group.make, {
              children: [
                JsxRuntime.jsxs(Stack.make, {
                  children: [
                    JsxRuntime.jsx(OrderSupplierInformationCard.make, {
                      editable: OrderStatus.has(statuses, "DRAFT")
                    }),
                    JsxRuntime.jsx(OrderInformationCard.make, {
                      editable: editable && !OrderEdit.isLimitedEdition(statuses),
                      receptionFinishedAt: props.receptionFinishedAt,
                      updatedAt: updatedAt
                    }),
                    JsxRuntime.jsx(OrderMemoCard.make, {})
                  ],
                  space: "medium"
                }),
                JsxRuntime.jsx(Stack.make, {
                  children: JsxRuntime.jsx(OrderConditionAndNotesCard.make, {
                    editable: editable && !OrderEdit.isLimitedEdition(statuses)
                  }),
                  space: "medium"
                })
              ],
              grid: [
                "30%",
                "70%"
              ],
              wrap: false,
              spaceX: "medium"
            })
          ],
          space: "medium"
        }),
        title: formattedTitle,
        renderTitleEnd: renderPageTitleEnd,
        renderActions: renderPageActions
      })
    ]
  });
}

let make = React.memo(OrderEditPage, (oldProps, newProps) => {
  if (oldProps.id === newProps.id && oldProps.name === newProps.name && oldProps.updatedAt === newProps.updatedAt && oldProps.statuses === newProps.statuses) {
    return oldProps.cartRowErrors === newProps.cartRowErrors;
  } else {
    return false;
  }
});

export {
  make,
}
/* make Not a pure module */
